import { useCallback, useContext } from 'react';
import { FixedNumber } from '@ethersproject/bignumber';
import {
  OracleContext,
  OracleGetGasPriceResult,
} from '@providers/OracleProvider';
import { CronosEthereumTransferType, TokenAsset } from '@types';
import useCalculateGasFeeInEther, {
  UseCalculateGasFeeInEtherConfig,
} from './useCalculateGasFeeInEther';
import useGetTokenQuoteConfig from './useGetTokenQuoteConfig';

interface CalculateCronosEthereumBridgeFeeResult {
  amount: string;
  ok: boolean;
  tokenAsset: TokenAsset;
}

interface UseCalculateCronosEthereumBridgeFeeConfig
  extends UseCalculateGasFeeInEtherConfig {
  gasPriceSpeed: keyof Omit<OracleGetGasPriceResult, 'blockNum'>;
}

/*
 High level overview of how to math out the bridge fee:

 Example 1 - USD-based pricing
 1. User selects CRO
 2. Make Oracle requests
  - GET /quotes/ETH-USD and GET /quotes/USD-CRO
  - GET /gasprice
 3. Fast Gas Price x Gas Limit x Gwei to ETH conversion = Gas Fee in ETH
 4. ETH-USD x USD-CRO = ETH-CRO
 5. 1 / ETH-CRO = CRO-ETH
 6. Gas Fee in ETH divided by CRO-ETH = Number of tokens deducted for Bridge Fee

 Example 2 - ETH-based pricing
 1. User selects WBTC
 2. Make Oracle requests
  - GET /quotes/ETH-BTC
  - GET /gasprice
 3. Fast Gas Price x Gas Limit x Gwei to ETH conversion = Gas Fee in ETH
 4. 1 / ETH-BTC = BTC-ETH
 5. Gas Fee in ETH divided by BTC-ETH = Number of tokens deducted for Bridge Fee
*/
function useCalculateCronosEthereumBridgeFee(
  config: UseCalculateCronosEthereumBridgeFeeConfig
): (
  tokenAsset: TokenAsset,
  transferType: CronosEthereumTransferType
) => Promise<CalculateCronosEthereumBridgeFeeResult> {
  const { getQuote, getGasPrice } = useContext(OracleContext);
  const getTokenQuoteConfig = useGetTokenQuoteConfig();
  const calculateGasFeeInEther = useCalculateGasFeeInEther(config);
  const { gasPriceSpeed } = config;

  return useCallback(
    async (
      tokenAsset: TokenAsset,
      transferType: CronosEthereumTransferType
    ): Promise<CalculateCronosEthereumBridgeFeeResult> => {
      async function getTokenAssetPriceInEther(): Promise<
        | { ok: false; priceInEther: null }
        | { ok: true; priceInEther: FixedNumber }
      > {
        const quoteTokenConfig = getTokenQuoteConfig(tokenAsset);

        if (!quoteTokenConfig) {
          return { ok: false, priceInEther: null };
        }

        const { baseTokenSymbol, quoteTokenSymbol, toPriceInEth } =
          quoteTokenConfig;

        if (baseTokenSymbol === 'ETH' && quoteTokenSymbol === 'ETH') {
          return { ok: true, priceInEther: FixedNumber.from(1) };
        }

        const [
          { data: tokenPriceData, ok: tokenPriceOk },
          { data: ethUsdPriceData, ok: ethUsdPriceOk },
        ] = await Promise.all([
          getQuote(baseTokenSymbol, quoteTokenSymbol),
          getQuote('ETH', 'USD'),
        ]);

        if (
          !tokenPriceOk ||
          !tokenPriceData?.aggregatedPrice ||
          !ethUsdPriceOk ||
          !ethUsdPriceData?.aggregatedPrice
        ) {
          return { ok: false, priceInEther: null };
        }

        const tokenPriceInEther = toPriceInEth(
          FixedNumber.from(`${tokenPriceData.aggregatedPrice}`),
          FixedNumber.from(`${ethUsdPriceData.aggregatedPrice}`)
        );

        return { ok: true, priceInEther: tokenPriceInEther };
      }

      // Get gas price
      const { data: gasPriceData, ok: gasPriceOk } = await getGasPrice();

      if (!gasPriceOk || !gasPriceData?.[gasPriceSpeed]) {
        return { amount: '', ok: false, tokenAsset };
      }

      // Get price of the token asset in ether
      const { priceInEther: tokenPriceInEth, ok: tokenPriceOk } =
        await getTokenAssetPriceInEther();

      if (!tokenPriceOk || !tokenPriceInEth) {
        return { amount: '', ok: false, tokenAsset };
      }

      // Calculate gas fee in ether of the transfer type
      const gasFeeInEth = calculateGasFeeInEther(
        transferType,
        gasPriceData[gasPriceSpeed]
      );
      // Get number of tokens used for bridge fee by dividing Gas Fee in Eth with Token Price in Eth
      const bridgeFeeInTokenAsset = FixedNumber.from(gasFeeInEth)
        .divUnsafe(tokenPriceInEth)
        .round(tokenAsset.decimals);

      return {
        amount: bridgeFeeInTokenAsset.toString(),
        ok: true,
        tokenAsset: { ...tokenAsset },
      };
    },
    [
      calculateGasFeeInEther,
      gasPriceSpeed,
      getGasPrice,
      getQuote,
      getTokenQuoteConfig,
    ]
  );
}

export default useCalculateCronosEthereumBridgeFee;
